import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_DIVIDER_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState } from '../../utils/listPageState'
import { formatNumber } from '../../utils/numberDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchStatus: '',
}

const SEARCH_SELECT_PARAMS = {
    searchStatus: 'searchStatus',
}

const APP_GIFTICON_COMPANY_REFUND_TABLE_COLUMNS = [
    { width: 70 },
    { width: 220 },
    { width: 320 },
    { width: 90 },
    { width: 120 },
    { width: 110 },
    { width: 180 },
    { width: 180 },
    { width: 120 },
]

const APP_GIFTICON_COMPANY_REFUND_TABLE_MIN_WIDTH = APP_GIFTICON_COMPANY_REFUND_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const APP_GIFTICON_COMPANY_REFUND_TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const APP_GIFTICON_COMPANY_REFUND_LABEL_CELL_SX = {
    whiteSpace: 'nowrap',
}

const getRefundProductLabel = (row) => {
    const product = row.gifticon_order_item?.gifticon_product
    const brandName = product?.brandName || ''
    const goodsName = product?.goodsName || ''

    if (brandName && goodsName) {
        return `[${brandName}] ${goodsName}`
    }

    return brandName || goodsName || '-'
}

export default function AppGifticonCompanyOrderRefunds() {
    const location = useLocation()
    const history = useHistory()
    const initialListState = createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText || '')
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery || '')
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    
    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/gifticonOrderRefund/getList', {
                page: curPage, offset: offset, searchText: searchQuery, searchSelect: searchSelect
            })
            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '환불 요청 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '환불 요청 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: SEARCH_SELECT_PARAMS,
        })

        if (location.search === nextSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [curPage, history, location.pathname, location.search, location.state, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
        setSearchQuery(searchText)
        setCurPage(1)
    }

    // 검색어와 필터를 실제 조회 조건으로 확정하고 첫 페이지부터 다시 본다.
    const searchButtonClick = () => {
        if (curPage === 1 && searchQuery === searchText) {
            getData()
            return
        }

        setSearchQuery(searchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        const isInitialState = curPage === 1 && !searchText && !searchQuery && !searchSelect.searchStatus

        if (isInitialState) {
            getData()
            return
        }

        setSearchText('')
        setSearchQuery('')
        setSearchSelect({ ...DEFAULT_SEARCH_SELECT })
        setCurPage(1)
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || String(searchSelect.searchStatus) !== String(DEFAULT_SEARCH_SELECT.searchStatus)

    // 환불 요청 목록의 조회 실패와 빈 결과를 운영자가 구분할 수 있게 안내한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="환불 요청 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 환불 요청이 없습니다."
                    description="기업명 또는 환불상태를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={loading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 환불 요청이 없습니다."
                description="환불 요청이 발생하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>환불 요청 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={loading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="기업명"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={loading}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={loading}><SearchIcon /></IconButton>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            <TextField
                                label="환불상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                                disabled={loading}
                            >
                                <MenuItem value={''}>전체</MenuItem>
                                <MenuItem value={0}>환불요청</MenuItem>
                                <MenuItem value={1}>환불완료</MenuItem>
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: APP_GIFTICON_COMPANY_REFUND_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                            <colgroup>
                                {APP_GIFTICON_COMPANY_REFUND_TABLE_COLUMNS.map((column, index) => (
                                    <col key={`app-gifticon-company-refund-column-${index}`} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>기업명</TableCell>
                                    <TableCell>기프티콘</TableCell>
                                    <TableCell>수량</TableCell>
                                    <TableCell>가격</TableCell>
                                    <TableCell>환불상태</TableCell>
                                    <TableCell>요청일</TableCell>
                                    <TableCell>환불일</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow key={row.orderItemRefundId}>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell sx={APP_GIFTICON_COMPANY_REFUND_TEXT_CELL_SX}>{row.gifticon_order_item?.gifticon_order?.company?.companyName || '-'}</TableCell>
                                            <TableCell sx={APP_GIFTICON_COMPANY_REFUND_TEXT_CELL_SX}>{getRefundProductLabel(row)}</TableCell>
                                            <TableCell sx={APP_GIFTICON_COMPANY_REFUND_LABEL_CELL_SX}>{row.refundQuantity ?? '-'}</TableCell>
                                            <TableCell sx={APP_GIFTICON_COMPANY_REFUND_LABEL_CELL_SX}>{formatNumber(row.refundAmount)}</TableCell>
                                            <TableCell sx={APP_GIFTICON_COMPANY_REFUND_LABEL_CELL_SX}>{row.status === 0 ? '환불요청' : '환불완료'}</TableCell>
                                            <TableCell sx={APP_GIFTICON_COMPANY_REFUND_LABEL_CELL_SX}>{row.regTime || '-'}</TableCell>
                                            <TableCell sx={APP_GIFTICON_COMPANY_REFUND_LABEL_CELL_SX}>{row.refundTime || '-'}</TableCell>
                                            <TableCell align="right">
                                                <ListActionGroup>
                                                    <EditIcon path={'appGifticonCompanyOrderRefund'} id={row.orderItemRefundId} search={location.search} searchText={searchQuery} searchSelect={searchSelect} curPage={curPage} disabled={loading}/>
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
